import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/auth";
import { Plus, Search, Save, ShieldCheck } from "lucide-react";

const fieldClass =
  "w-full rounded-lg border border-zinc-700 bg-zinc-900 " +
  "px-3 py-2 text-sm text-white focus:outline-none " +
  "focus:border-blue-500";

const getId = (record) =>
  String(record?.id || record?._id || "");

const asList = (data) =>
  Array.isArray(data)
    ? data
    : Array.isArray(data?.items)
      ? data.items
      : [];

async function loadAll(endpoint) {
  const records = [];
  const seen = new Set();
  const limit = 500;

  for (let skip = 0; skip < 100000; skip += limit) {
    const response = await api.get(endpoint, {
      params: { limit, skip },
    });

    const batch = asList(response.data);

    for (const item of batch) {
      const id = getId(item);
      if (id && !seen.has(id)) {
        seen.add(id);
        records.push(item);
      }
    }

    if (batch.length < limit) return records;
  }

  throw new Error("Pagination safety limit reached");
}

export default function ReportingGroupEditor({
  groups = [],
  onSaved,
}) {
  const [editingId, setEditingId] = useState("");
  const [clients, setClients] = useState([]);
  const [sites, setSites] = useState([]);
  const [clientId, setClientId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const editableGroups = groups.filter(
    (group) => group.client_id && group.id
  );

  const editingGroup = groups.find(
    (group) => group.id === editingId
  );

  const resetEditor = () => {
    setEditingId("");
    setClientId("");
    setGroupName("");
    setSelected([]);
    setSearch("");
    setError("");
    setSuccess("");
  };

  const beginEditing = (id) => {
    const group = groups.find(
      (item) => item.id === id && item.client_id
    );

    if (!group) {
      setError("This group cannot be edited in single-client mode.");
      return;
    }

    if (group.report_settings?.enabled !== false) {
      setError("Editing requires a group with delivery disabled.");
      return;
    }

    setEditingId(group.id);
    setClientId(String(group.client_id));
    setGroupName(group.name || "");
    setSelected([...(group.site_ids || [])]);
    setSearch("");
    setError("");
    setSuccess("");
  };

  useEffect(() => {
    let active = true;

    async function load() {
      setLoading(true);
      setError("");

      try {
        const [clientRecords, siteRecords] = await Promise.all([
          loadAll("/clients"),
          loadAll("/sites"),
        ]);

        if (!active) return;

        setClients(clientRecords);
        setSites(siteRecords);
      } catch (err) {
        if (active) {
          setError("Unable to load clients or locations.");
        }
      } finally {
        if (active) setLoading(false);
      }
    }

    load();

    return () => {
      active = false;
    };
  }, []);

  const visibleSites = useMemo(() => {
    const term = search.trim().toLowerCase();

    return sites.filter((site) => {
      const siteClient = String(site.client_id || "");
      const matchesClient = clientId && siteClient === clientId;

      const name = String(
        site.name || site.site_name || ""
      ).toLowerCase();

      return matchesClient && (
        !term || name.includes(term)
      );
    });
  }, [sites, clientId, search]);

  const toggleSite = (id) => {
    setSelected((previous) =>
      previous.includes(id)
        ? previous.filter((item) => item !== id)
        : [...previous, id]
    );
  };

  const saveGroup = async () => {
    setError("");
    setSuccess("");

    if (!clientId) {
      setError("Select a client.");
      return;
    }

    if (!groupName.trim()) {
      setError("Enter a reporting group name.");
      return;
    }

    if (!selected.length) {
      setError("Select at least one location.");
      return;
    }

    if (selected.length > 100) {
      setError("A reporting group can contain up to 100 locations.");
      return;
    }

    if (editingId && !editingGroup) {
      setError("The selected group is no longer available.");
      return;
    }

    if (editingId && editingGroup.client_id !== clientId) {
      setError("Changing a group's client is not supported.");
      return;
    }

    if (editingId && editingGroup.report_settings?.enabled !== false) {
      setError("Editing requires delivery to remain disabled.");
      return;
    }

    setSaving(true);

    try {
      const defaultSettings = {
          enabled: false,
          mode: "consolidated",
          frequency: "daily",
          send_time: "08:00",
          report_cutoff_time: "07:00",
          timezone: "Australia/Sydney",
          require_finalized: true,
          include_patrols: true,
          include_incidents: false,
          to_emails: [],
          cc_emails: [],
          bcc_emails: [],
      };

      let settings = defaultSettings;

      if (editingId) {
        const response = await api.get(
          `/reporting-groups/${encodeURIComponent(editingId)}`
        );

        const latest = response.data;

        if (
          String(latest.id) !== String(editingId) ||
          String(latest.client_id) !== String(clientId)
        ) {
          throw new Error(
            "Group identity or client ownership changed. Refresh before editing."
          );
        }

        const originalSettings = editingGroup.report_settings || {};
        const latestSettings = latest.report_settings || {};

        if (
          JSON.stringify(latestSettings) !==
          JSON.stringify(originalSettings)
        ) {
          throw new Error(
            "Reporting settings changed since this page loaded. Refresh before editing."
          );
        }

        if (
          latest.name !== editingGroup.name ||
          JSON.stringify(latest.site_ids || []) !==
          JSON.stringify(editingGroup.site_ids || [])
        ) {
          throw new Error(
            "Group membership or name changed. Refresh before editing."
          );
        }

        if (latestSettings.enabled !== false) {
          throw new Error(
            "Editing blocked because automatic delivery is not disabled."
          );
        }

        settings = { ...latestSettings, enabled: false };
      }

      const payload = {
        name: groupName.trim(),
        client_id: clientId,
        site_ids: selected,
        report_settings: settings,
      };

      if (editingId) {
        await api.put(
          `/reporting-groups/${encodeURIComponent(editingId)}`,
          payload
        );
      } else {
        await api.post("/reporting-groups", payload);
      }

      const wasEditing = Boolean(editingId);

      resetEditor();

      setSuccess(
        wasEditing
          ? "Reporting group updated. Automatic delivery remains disabled."
          : "Reporting group created. Automatic delivery remains disabled."
      );

      if (onSaved) await onSaved();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : err?.message ||
            "Unable to save reporting group. Check site ownership."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Plus className="w-4 h-4 text-blue-400" />
        <h3 className="font-semibold">
          {editingId ? "Edit reporting group" : "Create reporting group"}
        </h3>
      </div>

      <p className="text-sm text-zinc-400">
        Create a consolidated reporting run for locations
        belonging to one verified client.
      </p>

      <label className="block text-sm">
        Group action
        <select
          value={editingId}
          onChange={(event) => {
            if (event.target.value) {
              beginEditing(event.target.value);
            } else {
              resetEditor();
            }
          }}
          className={fieldClass + " mt-2"}
          disabled={loading || saving}
        >
          <option value="">Create new reporting group</option>
          {editableGroups.map((group) => (
            <option key={group.id} value={group.id}>
              Edit: {group.name}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        Client
        <select
          value={clientId}
          onChange={(event) => {
            setClientId(event.target.value);
            setSelected([]);
          }}
          className={fieldClass + " mt-2"}
          disabled={loading || saving || Boolean(editingId)}
        >
          <option value="">Select client</option>
          {clients.map((client) => (
            <option key={getId(client)} value={getId(client)}>
              {client.name || client.client_name || getId(client)}
            </option>
          ))}
        </select>
      </label>

      <label className="block text-sm">
        Reporting group name
        <input
          value={groupName}
          onChange={(event) => setGroupName(event.target.value)}
          placeholder="e.g. Hunter Water - Newcastle Patrol Run"
          maxLength={150}
          className={fieldClass + " mt-2"}
          disabled={saving}
        />
      </label>

      <div className="relative">
        <Search
          className="absolute left-3 top-3 w-4 h-4 text-zinc-500"
        />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search client locations"
          className={fieldClass + " pl-10"}
          disabled={!clientId || loading || saving}
        />
      </div>

      <div className="max-h-64 overflow-y-auto rounded-lg
                      border border-zinc-700 divide-y divide-zinc-800">
        {loading ? (
          <p className="p-4 text-sm text-zinc-400">
            Loading locations...
          </p>
        ) : !clientId ? (
          <p className="p-4 text-sm text-zinc-400">
            Select a client to view locations.
          </p>
        ) : visibleSites.length === 0 ? (
          <p className="p-4 text-sm text-zinc-400">
            No matching locations.
          </p>
        ) : visibleSites.map((site) => {
          const id = getId(site);

          return (
            <label
              key={id}
              className="flex items-center gap-3 p-3 text-sm
                         hover:bg-zinc-800/50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(id)}
                onChange={() => toggleSite(id)}
                disabled={saving}
              />
              {site.name || site.site_name || id}
            </label>
          );
        })}
      </div>

      <p className="text-xs text-zinc-400">
        {selected.length} locations selected.
        {groups.length} existing reporting groups.
      </p>

      <div className="flex items-center gap-2 text-xs text-emerald-400">
        <ShieldCheck className="w-4 h-4" />
        Backend verification required before a group is accepted.
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-400">
          {error}
        </p>
      )}

      {success && (
        <p role="status" className="text-sm text-emerald-400">
          {success}
        </p>
      )}

      <button
        type="button"
        onClick={saveGroup}
        disabled={loading || saving}
        className="flex items-center gap-2 rounded-lg
                   bg-blue-600 hover:bg-blue-500 px-4 py-2
                   text-sm font-medium disabled:opacity-50"
      >
        <Save className="w-4 h-4" />
        {saving
          ? "Saving..."
          : editingId
            ? "Update Reporting Group"
            : "Save Reporting Group"}
      </button>

      {editingId && (
        <button
          type="button"
          onClick={resetEditor}
          disabled={saving}
          className="ml-3 rounded-lg border border-zinc-700
                     px-4 py-2 text-sm hover:bg-zinc-800"
        >
          Cancel Editing
        </button>
      )}

      <p className="text-xs text-zinc-500">
        Creating or updating a group saves configuration in RapidX.
        It does not activate scheduled reporting or email delivery.
      </p>
    </div>
  );
}
